import { useState, useEffect, useCallback } from 'react';
import { ColumnConfig } from '../types/columns';
import {
  loadColumnPreferences,
  saveColumnPreferences,
  resetColumnPreferences,
} from '../utils/columnStorage';

export const useColumnConfig = () => {
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load column preferences on mount
  useEffect(() => {
    const loadedColumns = loadColumnPreferences();
    setColumns(loadedColumns);
    setIsLoading(false);
  }, []);

  // Get only visible columns
  const visibleColumns = columns.filter(col => col.visible);

  // Update column visibility
  const updateColumnVisibility = useCallback(
    (columnId: string, visible: boolean) => {
      setColumns(prevColumns => {
        const updatedColumns = prevColumns.map(col =>
          col.id === columnId ? { ...col, visible } : col
        );
        saveColumnPreferences(updatedColumns);
        return updatedColumns;
      });
    },
    []
  );

  // Bulk update columns
  const updateColumns = useCallback((newColumns: ColumnConfig[]) => {
    setColumns(newColumns);
    saveColumnPreferences(newColumns);
  }, []);

  // Reset to defaults
  const resetColumns = useCallback(() => {
    const defaultColumns = resetColumnPreferences();
    setColumns(defaultColumns);
    return defaultColumns;
  }, []);

  // Get column config by ID
  const getColumnConfig = useCallback(
    (columnId: string) => {
      return columns.find(col => col.id === columnId);
    },
    [columns]
  );

  return {
    columns,
    visibleColumns,
    isLoading,
    updateColumnVisibility,
    updateColumns,
    resetColumns,
    getColumnConfig,
  };
};
